import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import { useTableManagement, resolveTableLabel } from '../../lib/table-hooks';
import { PayTableModal } from '../../components/admin/PayTableModal';
import { CabanaModifyOrderModal } from '../../components/admin/CabanaModifyOrderModal';
import { CabanaAddItemsModal } from '../../components/admin/CabanaAddItemsModal';
import { VoidConfirmModal } from '../../components/admin/VoidConfirmModal';
import { logVoidAudit, updateVoidedColumns } from '../../lib/void-audit';
import type { ActionResult } from '../../lib/admin-hooks';
import type { TableWithOrder } from '../../lib/types';
import { useTenant } from '../../lib/tenant-context';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { Clock, CheckCircle2, AlertTriangle, ShoppingBag, Bike, Store, ChefHat, X, Receipt, Utensils, ShoppingBasket, Printer, Pencil, Plus } from 'lucide-react';
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function CajaOverviewPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    refetchOrders
  } = useAdminOrders(restaurant?.id);
  const {
    tables,
    refetch: refetchTables
  } = useTableManagement(restaurant?.id);
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'new' | 'preparing' | 'ready' | 'delayed'>('all');
  const [showPayModal, setShowPayModal] = useState(false);
  const [payTable, setPayTable] = useState<TableWithOrder | null>(null);
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  const isCongelados = String(restaurant?.id) === '18' || restaurant?.slug === 'heladeria-congelados';
  const isNatucafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  // Rancho San Francisco (id 28) — Control de Caja muestra pedidos
  // bucketeados por tipo: Mesas (dine_in), Para llevar (pickup),
  // Express (delivery). Cobrar/Facturar y la impresión siguen
  // funcionando por las rutas existentes (PayTableModal + processPayment).
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || restaurant?.slug === 'rancho-san-francisco';
  // La Cabaña Parrillera (restaurant_id 24) — exclusive feature:
  // cashier can modify or cancel any active order from this page.
  const isCabanaParrillera = String(restaurant?.id) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  const [modifyOrderId, setModifyOrderId] = useState<string | null>(null);
  const [addItemsOrderId, setAddItemsOrderId] = useState<string | null>(null);
  const isSimplifiedFlow = isCapricho || isCongelados;
  const [channelFilter, setChannelFilter] = useState<'all' | 'marketplace' | 'restaurant'>('all');
  const [ranchoBucket, setRanchoBucket] = useState<'all' | 'mesas' | 'pickup' | 'delivery'>('all');
  // Rancho — set of order ids currently being printed (to disable the
  // "Imprimir comanda" button while the insert is in flight).
  const [printingOrderIds, setPrintingOrderIds] = useState<Set<string>>(new Set());
  // Rancho — order id currently open in the modify modal.
  const [ranchoModifyOrderId, setRanchoModifyOrderId] = useState<string | null>(null);
  // Void Auth State for NatuCafe order cancellation
  const [voidModalState, setVoidModalState] = useState<{
    isOpen: boolean;
    orderId: string | null;
    originalAmount: number;
  }>({
    isOpen: false,
    orderId: null,
    originalAmount: 0
  });
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const handleOpenPay = useCallback((order: any) => {
    // Marketplace orders (delivery / pickup) have no table_id. We still
    // want to allow Cobrar/Facturar from caja for them — build a
    // synthetic "table" referencing the order so PayTableModal can load
    // the items by order_id and process payment normally.
    if (!order?.table_id) {
      setPayTable({
        id: '',
        restaurant_id: String(restaurant?.id || ''),
        // Prefix marks this as a non-table sentinel so the modal header
        // shows "Pedido #..." instead of "Mesa ...".
        table_number: `order:${order.id}`,
        table_name: order.delivery_type === 'delivery' ? `Pedido Delivery #${String(order.order_number || '').split('-').pop() || ''}` : order.delivery_type === 'pickup' ? `Pedido para Llevar #${String(order.order_number || '').split('-').pop() || ''}` : `Pedido #${String(order.order_number || '').split('-').pop() || ''}`,
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: order,
        current_order_items: order.items || [],
        status: 'occupied',
        order_channel: 'marketplace',
        time_open_minutes: null,
        item_count: (order.items || []).length
      } as TableWithOrder);
      setShowPayModal(true);
      return;
    }
    const matched = tables.find(t => String(t.id) === String(order.table_id));
    if (matched) {
      setPayTable(matched);
    } else {
      setPayTable({
        id: String(order.table_id),
        restaurant_id: String(restaurant?.id || ''),
        table_number: order.table_id,
        table_name: null,
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: order,
        current_order_items: order.items || [],
        status: 'occupied',
        order_channel: order.source === 'waiter' ? 'waiter' : 'qr',
        time_open_minutes: null,
        item_count: (order.items || []).length
      } as TableWithOrder);
    }
    setShowPayModal(true);
  }, [tables, restaurant?.id, addToast]);
  // ─── Rancho San Francisco — Imprimir comanda desde Control de Caja ─
  // Inserta una fila pending en order_print_jobs con un trigger único
  // (manual_caja_<timestamp>) para que el bridge Python imprima el
  // ticket. Resuelve tracking_token (UUID) si la orden trae id BIGINT,
  // igual que en TableManagementPage. Sólo Rancho — cero impacto en el
  // resto. La caja y el pago siguen funcionando idénticos.
  const handleImprimirComandaRancho = useCallback(async (order: any) => {
    if (!isRanchoSanFrancisco) return;
    if (!order?.id) return;
    setPrintingOrderIds(prev => new Set(prev).add(String(order.id)));
    try {
      // Resolver tracking_token (UUID) — order_print_jobs.order_id es UUID.
      let orderRefUuid: string | null = order.tracking_token ?? null;
      if (!orderRefUuid && order.id) {
        try {
          const {
            data: row
          } = await supabase.from('orders').select('tracking_token').eq('id', order.id).maybeSingle();
          orderRefUuid = (row as any)?.tracking_token ?? null;
        } catch (e) {
          console.warn('[RANCHO_PRINT][caja] tracking_token fetch failed (non-blocking):', e);
        }
      }
      if (!orderRefUuid) orderRefUuid = order.id ?? null;
      if (!orderRefUuid) {
        addToast('error', 'Orden sin identificador (no se puede imprimir)');
        return;
      }
      const triggerValue = `manual_caja_${Date.now()}`;
      const deliveryType = order.delivery_type || 'dine_in';
      const customerName = order.customer_name || (deliveryType === 'dine_in' && order.table_id ? `Mesa ${order.table_id}` : 'Cliente');
      // Rancho — Enriquecemos cada ítem del payload con (a) las opciones
      // seleccionadas concatenadas al nombre ("Casado · Chicharrón") y
      // (b) un prefijo "[Arma]" en los ítems de Arma tu plato
      // (menu_item_id NULL) para que NO se mezclen visualmente con
      // platillos normales (ej. "Kilo de chicharrones") en la comanda
      // impresa. El bridge imprime item_name_snapshot tal cual.
      const enrichedItems = (order.items ?? []).map((it: any) => {
        const baseName = String(it.item_name_snapshot || it.item_name || '').trim();
        const itemOptions = Array.isArray(it.options) ? it.options : [];
        const optsSummary = itemOptions.map((o: any) => String(o.option_name_snapshot || o.option_name || '').trim()).filter((s: string) => s.length > 0).join(' · ');
        const isArma = it.menu_item_id == null;
        const armaPrefix = isArma ? '[Arma] ' : '';
        const enrichedName = optsSummary ? `${armaPrefix}${baseName} · ${optsSummary}` : `${armaPrefix}${baseName}`;
        return {
          ...it,
          is_arma_tu_plato: isArma,
          original_item_name: baseName,
          item_name_snapshot: enrichedName
        };
      });
      const {
        error
      } = await supabase.from('order_print_jobs').insert({
        restaurant_id: 28,
        order_id: orderRefUuid,
        order_number: order.order_number ?? null,
        status: 'pending',
        payload: {
          order,
          order_internal_id: order.id ?? null,
          order_number: order.order_number ?? null,
          created_at: order.created_at ?? null,
          customer_name: customerName,
          customer_phone: order.customer_phone ?? null,
          delivery_type: deliveryType,
          payment_method: order.payment_method ?? 'cash',
          table_number: order.table_id ?? null,
          subtotal: order.subtotal ?? 0,
          delivery_fee: order.delivery_fee ?? 0,
          total: order.total ?? 0,
          notes: order.notes ?? null,
          items: enrichedItems,
          trigger: triggerValue
        }
      });
      if (error) {
        console.error('[RANCHO_PRINT][caja] insert error:', error);
        addToast('error', `No se pudo imprimir: ${error.message || 'error desconocido'}`);
      } else {
        addToast('success', '🖨 Enviado a la impresora');
      }
    } catch (e: any) {
      console.error('[RANCHO_PRINT][caja] exception:', e);
      addToast('error', e?.message || 'Error al enviar a la impresora');
    } finally {
      setPrintingOrderIds(prev => {
        const next = new Set(prev);
        next.delete(String(order.id));
        return next;
      });
    }
  }, [isRanchoSanFrancisco, addToast]);
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3370" data-mp="1uet65dprkioz">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-3371" data-mp="1pg02ed0a4620">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-3372" data-mp="11qnleqypmtp5" />
        </div>
      </AdminLayout>;
  }
  // Filter active orders
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  // Channel classification (NatuCafé)
  const isMarketplaceOrder = (o: any) => o.delivery_type === 'delivery' || o.delivery_type === 'pickup';
  const isRestaurantOrder = (o: any) => o.delivery_type === 'dine_in' || !isMarketplaceOrder(o) && (o.table_id || o.source === 'waiter' || o.source === 'qr');
  const marketplaceCount = activeOrders.filter(isMarketplaceOrder).length;
  const restaurantCount = activeOrders.filter(isRestaurantOrder).length;
  // Rancho — bucket classification by delivery_type
  const isRanchoMesaOrder = (o: any) => o.delivery_type === 'dine_in' || o.delivery_type === 'mesa' || !o.delivery_type && o.table_id;
  const isRanchoPickupOrder = (o: any) => o.delivery_type === 'pickup';
  const isRanchoDeliveryOrder = (o: any) => o.delivery_type === 'delivery';
  const ranchoMesasCount = activeOrders.filter(isRanchoMesaOrder).length;
  const ranchoPickupCount = activeOrders.filter(isRanchoPickupOrder).length;
  const ranchoDeliveryCount = activeOrders.filter(isRanchoDeliveryOrder).length;
  const channeledOrders = isNatucafe ? activeOrders.filter((o: any) => {
    if (channelFilter === 'all') return true;
    if (channelFilter === 'marketplace') return isMarketplaceOrder(o);
    if (channelFilter === 'restaurant') return isRestaurantOrder(o);
    return true;
  }) : isRanchoSanFrancisco ? activeOrders.filter((o: any) => {
    if (ranchoBucket === 'all') return true;
    if (ranchoBucket === 'mesas') return isRanchoMesaOrder(o);
    if (ranchoBucket === 'pickup') return isRanchoPickupOrder(o);
    if (ranchoBucket === 'delivery') return isRanchoDeliveryOrder(o);
    return true;
  }) : activeOrders;
  const sortedOrders = [...channeledOrders].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const newOrders = channeledOrders.filter(o => o.status === 'new' || o.status === 'accepted');
  const preparingOrders = channeledOrders.filter(o => o.status === 'preparing');
  const readyOrders = channeledOrders.filter(o => o.status === 'ready' || o.status === 'searching_driver');
  const delayedOrders = channeledOrders.filter(o => {
    const waitMinutes = Math.floor((new Date().getTime() - new Date(o.created_at).getTime()) / 60000);
    return waitMinutes >= 15;
  });
  const getShortOrderNumber = (orderNumber: string) => {
    if (!orderNumber) return '';
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  // Action handler — now with feedback
  const handleOrderAction = async (orderId: string, action: string) => {
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    let result: ActionResult = {
      success: false,
      error: 'Acción desconocida'
    };
    try {
      switch (action) {
        case 'send_to_kitchen':
          {
            // Combined: accept + start preparation in one action
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '🔥 Enviado a cocina');
            break;
          }
        case 'start_preparation':
          result = await startOrderPreparation(orderId);
          if (result.success) addToast('success', '🔥 Enviado a cocina');
          break;
        case 'mark_ready':
          result = await markOrderReady(orderId);
          if (result.success) addToast('success', '✅ Pedido marcado como listo');
          break;
        case 'deliver':
          result = await deliverOrder(orderId);
          if (result.success) addToast('success', '🚀 Pedido entregado');
          break;
        case 'capricho_receive':
          {
            // Combined: accept + start preparation
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '✅ Pedido recibido');
            break;
          }
        case 'capricho_complete':
          {
            // Combined: mark ready + deliver
            result = await markOrderReady(orderId);
            if (result.success) {
              result = await deliverOrder(orderId);
            }
            if (result.success) addToast('success', '🎉 Pedido completado');
            break;
          }
        case 'cancel':
          const orderToCancel = orders.find((o: any) => String(o.id) === String(orderId));
          setVoidModalState({
            isOpen: true,
            orderId,
            originalAmount: orderToCancel?.total || 0
          });
          // Return early, the actual cancellation will happen in handleVoidConfirm
          setUpdatingOrders(prev => {
            const next = new Set(prev);
            next.delete(orderId);
            return next;
          });
          return;
      }
      if (!result.success && result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error inesperado');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  const handleVoidConfirm = async (authorizedBy: string, reason: string) => {
    if (!voidModalState.orderId || !restaurant?.id) return;
    const orderId = voidModalState.orderId;
    setVoidModalState({
      isOpen: false,
      orderId: null,
      originalAmount: 0
    });
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    try {
      await updateVoidedColumns('orders', Number(orderId), authorizedBy, reason);
      await logVoidAudit({
        restaurant_id: restaurant.id,
        order_id: Number(orderId),
        action_type: 'cancel_order',
        authorized_by: authorizedBy,
        reason,
        original_amount: voidModalState.originalAmount
      });
      const result = await cancelOrder(orderId);
      if (result.success) {
        addToast('success', '❌ Pedido anulado correctamente');
      } else if (result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error al anular pedido');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3373" data-mp="26uj7n4bvlzq2">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-3374" data-mp="2bovpxb1m9508">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-3375" data-mp="2gnq9wgbbkzlx">
          <div data-id="element-3376" data-mp="1nx88rku5p8b5">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-3377" data-mp="1nwoovyky49uh">
              Control de Caja
              <span className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-3378" data-mp="1suq8ckmaab6h">
                {activeOrders.length} activos
              </span>
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-3379" data-mp="34aydmtt3o0lu">
              Vista general de todas las estaciones y pedidos en curso
            </p>
          </div>
        </div>

        {/* NatuCafé channel tabs */}
        {isNatucafe && <div className="mb-6 flex flex-wrap gap-2 p-1.5 bg-[#141414] border border-white/5 rounded-2xl w-full sm:w-auto" data-id="element-3380" data-mp="oy5tmjow78qw">
            {([{
          key: 'all',
          label: 'Todos',
          count: activeOrders.length,
          icon: null
        }, {
          key: 'marketplace',
          label: 'Marketplace',
          count: marketplaceCount,
          icon: Bike
        }, {
          key: 'restaurant',
          label: 'En el restaurante',
          count: restaurantCount,
          icon: Store
        }] as const).map(tab => {
          const isActive = channelFilter === tab.key;
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setChannelFilter(tab.key)} className={cn('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex-1 sm:flex-none justify-center', isActive ? 'bg-white text-black shadow-md' : 'text-white/60 hover:text-white hover:bg-white/5')} data-id="element-3381" data-mp="1vi5fv4ee4ktt">
                  {Icon && <Icon className="w-4 h-4" data-id="element-3382" data-mp="2x5ishelgohkg" />}
                  <span data-id="element-3383" data-mp="2s897qc90ou2y">{tab.label}</span>
                  <span className={cn('min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-black flex items-center justify-center tabular-nums', isActive ? 'bg-black/10 text-black' : 'bg-white/10 text-white/70')} data-id="element-3384" data-mp="2s77ldbmjwl5x">
                    {tab.count}
                  </span>
                </button>;
        })}
          </div>}

        {/* Rancho San Francisco — bucket tabs (Mesas / Para llevar / Express) */}
        {isRanchoSanFrancisco && <div className="mb-6 flex flex-wrap gap-2 p-1.5 bg-[#141414] border border-white/5 rounded-2xl w-full sm:w-auto" data-id="element-3385" data-mp="rked3tufznq0">
            {([{
          key: 'all',
          label: 'Todos',
          count: activeOrders.length,
          icon: null
        }, {
          key: 'mesas',
          label: 'Mesas',
          count: ranchoMesasCount,
          icon: Utensils
        }, {
          key: 'pickup',
          label: 'Para llevar',
          count: ranchoPickupCount,
          icon: ShoppingBasket
        }, {
          key: 'delivery',
          label: 'Express',
          count: ranchoDeliveryCount,
          icon: Bike
        }] as const).map(tab => {
          const isActive = ranchoBucket === tab.key;
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setRanchoBucket(tab.key)} className={cn('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex-1 sm:flex-none justify-center', isActive ? 'bg-white text-black shadow-md' : 'text-white/60 hover:text-white hover:bg-white/5')} data-id="element-3386" data-mp="5bh2ezdr8qd8">
                  {Icon && <Icon className="w-4 h-4" data-id="element-3387" data-mp="3dq96p6fc39az" />}
                  <span data-id="element-3388" data-mp="2q27zq1xt00xo">{tab.label}</span>
                  <span className={cn('min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-black flex items-center justify-center tabular-nums', isActive ? 'bg-black/10 text-black' : 'bg-white/10 text-white/70')} data-id="element-3389" data-mp="2q0wz2aznn9as">
                    {tab.count}
                  </span>
                </button>;
        })}
          </div>}

        {/* Stats Row — clickable filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-id="element-3390" data-mp="3hgd8zzutjru0">
          <button onClick={() => setActiveFilter(activeFilter === 'new' ? 'all' : 'new')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-blue-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'new' ? 'border-blue-500 ring-2 ring-blue-500/40 bg-blue-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-3391" data-mp="3hifwod8i18vk">
            <div className="flex items-center justify-between mb-2" data-id="element-3392" data-mp="j4gogs4raz03">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-3393" data-mp="j5rs8ry7c6r7">
                <ShoppingBag className="w-4 h-4 text-blue-400" data-id="element-3394" data-mp="ecgvxkr0n8dj" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-3395" data-mp="acoiouqoge1u">
                {newOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-3396" data-mp="123cia2cpdbde">Pendientes</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'preparing' ? 'all' : 'preparing')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-amber-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'preparing' ? 'border-amber-500 ring-2 ring-amber-500/40 bg-amber-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-3397" data-mp="12104m1dz9n65">
            <div className="flex items-center justify-between mb-2" data-id="element-3398" data-mp="3abhby5v1odw6">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-3399" data-mp="3faasy89jin13">
                <ChefHat className="w-4 h-4 text-amber-400" data-id="element-3400" data-mp="31983dljc6ulf" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-3401" data-mp="368uriq0pdv90">
                {preparingOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-3402" data-mp="2riwwbq4cw5ab">En Cocina</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'ready' ? 'all' : 'ready')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-emerald-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'ready' ? 'border-emerald-500 ring-2 ring-emerald-500/40 bg-emerald-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-3403" data-mp="2wcrfmsg1ui8v">
            <div className="flex items-center justify-between mb-2" data-id="element-3404" data-mp="1u0q99xdy093c">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-3405" data-mp="1p1demqgtxxq7">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" data-id="element-3406" data-mp="23ye6qv2xnup7" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-3407" data-mp="1ywf2zk3a6vrq">
                {readyOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-3408" data-mp="27yoae139690w">Listos</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'delayed' ? 'all' : 'delayed')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-red-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'delayed' ? 'border-red-500 ring-2 ring-red-500/40 bg-red-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-3409" data-mp="27wu15k1cg5cn">
            <div className="flex items-center justify-between mb-2" data-id="element-3410" data-mp="6v286fybklzy">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center" data-id="element-3411" data-mp="6xet91zdjch8">
                <AlertTriangle className="w-4 h-4 text-red-400" data-id="element-3412" data-mp="bskgc44iix6w" />
              </div>
              <span className="text-2xl font-black tabular-nums text-red-400" data-id="element-3413" data-mp="fv7badwhcy8b">
                {delayedOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-red-400/70" data-id="element-3414" data-mp="3k9fb0wb5pb9w">
              Atrasados (&gt;15m)
            </p>
          </button>
        </div>

        {/* Active filter indicator */}
        {activeFilter !== 'all' && <div className="flex items-center gap-2 mb-4" data-id="element-3415" data-mp="3t3bqjs9tfpgc">
            <span className="text-sm text-white/50" data-id="element-3416" data-mp="2wkvyg4l2qcc0">Filtrando por:</span>
            <span className={cn('px-3 py-1 rounded-full text-xs font-bold', activeFilter === 'new' && 'bg-blue-500/20 text-blue-400', activeFilter === 'preparing' && 'bg-amber-500/20 text-amber-400', activeFilter === 'ready' && 'bg-emerald-500/20 text-emerald-400', activeFilter === 'delayed' && 'bg-red-500/20 text-red-400')} data-id="element-3417" data-mp="2witd3xuv97dm">
              {activeFilter === 'new' && `Pendientes (${newOrders.length})`}
              {activeFilter === 'preparing' && `En Cocina (${preparingOrders.length})`}
              {activeFilter === 'ready' && `Listos (${readyOrders.length})`}
              {activeFilter === 'delayed' && `Atrasados (${delayedOrders.length})`}
            </span>
            <button onClick={() => setActiveFilter('all')} className="text-white/30 hover:text-white/60 transition-colors" data-id="element-3418" data-mp="1wdqupdah0xtc">
              <X className="w-4 h-4" data-id="element-3419" data-mp="1mdqbrsgikjpo" />
            </button>
          </div>}

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 items-start" data-id="element-3420" data-mp="vbp4b9wndl35">
          {ordersLoading || stationsLoading ? [1, 2, 3, 4].map(i => <div key={i} className="h-64 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-3421" data-mp="15awi1y7anhqs" />) : (() => {
          const filteredOrders = sortedOrders.filter((order: any) => {
            if (activeFilter === 'all') return true;
            if (activeFilter === 'new') return order.status === 'new' || order.status === 'accepted';
            if (activeFilter === 'preparing') return order.status === 'preparing';
            if (activeFilter === 'ready') return order.status === 'ready' || order.status === 'searching_driver';
            if (activeFilter === 'delayed') {
              const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
              return waitMinutes >= 15;
            }
            return true;
          });
          return filteredOrders.length > 0 ? filteredOrders.map((order: any) => {
            const isUpdatingOrder = updatingOrders.has(order.id);
            const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
            const stationProgress = new Map<string, {
              total: number;
              ready: number;
              name: string;
            }>();
            order.items.forEach((item: any) => {
              const stationCode = item.station_code || 'sin_asignar';
              const stationName = stations.find(s => s.code === stationCode)?.name || (stationCode === 'sin_asignar' ? 'General' : stationCode);
              if (!stationProgress.has(stationCode)) {
                stationProgress.set(stationCode, {
                  total: 0,
                  ready: 0,
                  name: stationName
                });
              }
              const progress = stationProgress.get(stationCode)!;
              progress.total += item.quantity;
              if (item.status === 'ready' || item.status === 'served') {
                progress.ready += item.quantity;
              }
            });
            const allItemsReady = Array.from(stationProgress.values()).every(p => p.ready === p.total) && order.items.length > 0;
            // Calculate overall progress percentage
            const totalItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.total, 0);
            const readyItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.ready, 0);
            const overallPercentage = totalItems > 0 ? Math.round(readyItems / totalItems * 100) : 0;
            // Kitchen activity: any item has been touched by KDS
            // (waiter/dine_in orders skip 'preparing' status — items get
            //  worked on while order.status stays 'new')
            const hasKitchenActivity = order.items.some((it: any) => it.status === 'ready' || it.status === 'served' || it.status === 'in_progress' || it.status === 'preparing');
            const showProgressView = order.status === 'preparing' || order.status === 'ready' || hasKitchenActivity;
            let borderColor = 'border-white/10';
            if (order.status === 'new' || order.status === 'accepted') borderColor = 'border-blue-500';else if (order.status === 'preparing') borderColor = 'border-amber-500';else if (order.status === 'ready' || order.status === 'searching_driver' || allItemsReady) borderColor = 'border-emerald-500';
            // ── Simplified card (Capricho / Congelados) ──
            if (isSimplifiedFlow) {
              const caprichoStatus = order.status === 'new' || order.status === 'accepted' ? 'nuevo' : order.status === 'preparing' ? 'en_preparacion' : order.status === 'ready' || order.status === 'searching_driver' ? 'listo' : 'otro';
              return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', caprichoStatus === 'nuevo' ? 'border-blue-500' : caprichoStatus === 'en_preparacion' ? 'border-amber-500' : caprichoStatus === 'listo' ? 'border-emerald-500' : 'border-white/10')} data-id="element-3422" data-mp="3mj9afeh4j8kn">
                          {/* Header */}
                          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-white/[0.02]" data-id="element-3423" data-mp="21zm5pd2bgkr0">
                            <div className="flex items-center gap-2" data-id="element-3424" data-mp="1t0sddyw0qgj9">
                              <span className="text-lg font-black text-white tabular-nums" data-id="element-3425" data-mp="1o27f450bu6xg">
                                #{getShortOrderNumber(order.order_number)}
                              </span>
                              {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-3426" data-mp="clqvdzhqp44s">
                                  <Bike className="w-3 h-3" data-id="element-3427" data-mp="2t8yxna6ep9r1" /> Delivery
                                </span> : <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-3428" data-mp="2x9sjzz47aght">
                                  <ShoppingBag className="w-3 h-3" data-id="element-3429" data-mp="371erplhbxxft" /> Llevar
                                </span>}
                            </div>
                            <span className={cn('text-xl font-black tabular-nums', waitMinutes >= 15 ? 'text-red-500' : waitMinutes >= 10 ? 'text-amber-500' : 'text-emerald-500')} data-id="element-3430" data-mp="3gz2zbc01azfe">
                              {waitMinutes}m
                            </span>
                          </div>

                          {/* Customer */}
                          <div className="px-4 py-3 border-b border-white/5" data-id="element-3431" data-mp="1giyf1q0ax6sz">
                            <p className="text-base font-black text-white" data-id="element-3432" data-mp="1chd7mk6jdkq6">
                              {order.customer_name}
                            </p>
                            {order.customer_phone && <p className="text-xs text-white/40 mt-0.5" data-id="element-3433" data-mp="jt7lbszu3no8">
                                {order.customer_phone}
                              </p>}
                            <p className="text-sm font-black text-[#C8A96A] mt-1" data-id="element-3434" data-mp="ssm9umwfxo63">
                              ₡{order.total.toLocaleString()}
                            </p>
                          </div>

                          {/* Status */}
                          <div className={cn('px-4 py-2.5 border-b', caprichoStatus === 'nuevo' ? 'bg-blue-500/10 border-blue-500/20' : caprichoStatus === 'en_preparacion' ? 'bg-amber-500/10 border-amber-500/20' : caprichoStatus === 'listo' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-white/5 border-white/5')} data-id="element-3435" data-mp="248195xc4gs4d">
                            <span className={cn('text-xs font-black uppercase tracking-wider flex items-center gap-1.5', caprichoStatus === 'nuevo' ? 'text-blue-400' : caprichoStatus === 'en_preparacion' ? 'text-amber-400' : caprichoStatus === 'listo' ? 'text-emerald-400' : 'text-white/40')} data-id="element-3436" data-mp="2loyegjtz04d6">
                              {caprichoStatus === 'nuevo' && <>
                                  <Clock className="w-3.5 h-3.5" data-id="element-3437" data-mp="15f7jsnlmui3x" /> Nuevo pedido
                                </>}
                              {caprichoStatus === 'en_preparacion' && <>
                                  <ChefHat className="w-3.5 h-3.5" data-id="element-3438" data-mp="vow1fw17c6di" /> En
                                  preparación
                                </>}
                              {caprichoStatus === 'listo' && <>
                                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-3439" data-mp="1t5bhyfg1xpnj" /> Listo
                                  para entregar
                                </>}
                            </span>
                          </div>

                          {/* Items with toppings — excludes items cancelled
                                                      via the modify modal so the comanda matches the
                                                      real state of the order. */}
                          <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-3440" data-mp="1jmk7ccn46aww">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-3441" data-mp="1fk7domln63gh">
                              Detalle del pedido
                            </h4>
                            <div className="space-y-2.5" data-id="element-3442" data-mp="1oij16fr7ol4p">
                              {order.items.filter((item: any) => item.status !== 'cancelled').map((item: any) => <div key={item.id} data-id="element-3443" data-mp="37ynjhwfh6axa">
                                    <div className="flex justify-between text-sm" data-id="element-3444" data-mp="3c0qw4odvrlk5">
                                      <span className="text-white font-bold" data-id="element-3445" data-mp="3h03sbzqn64ze">
                                        {item.quantity}x{' '}
                                        {item.item_name_snapshot}
                                      </span>
                                      <span className="text-[#C8A96A] font-bold text-xs shrink-0" data-id="element-3446" data-mp="2ocr47l4u1p3z">
                                        ₡
                                        {(item.unit_price * item.quantity).toLocaleString()}
                                      </span>
                                    </div>
                                    {item.options && item.options.length > 0 && <div className="mt-1 ml-4 space-y-0.5" data-id="element-3447" data-mp="2ahyho2qiz8nw">
                                          {item.options.map((opt: any, idx: number) => <div key={idx} className="flex justify-between text-xs" data-id="element-3448" data-mp="1t2cfrey7n25o" data-mpi={idx}>
                                                <span className="text-amber-400/80" data-id="element-3449" data-mp="1k2pm5fbq30n6">
                                                  + {opt.option_name}
                                                </span>
                                                {opt.price_delta > 0 && <span className="text-white/30" data-id="element-3450" data-mp="15djinf5uqo9h">
                                                    +₡
                                                    {opt.price_delta.toLocaleString()}
                                                  </span>}
                                              </div>)}
                                        </div>}
                                    {item.notes && <p className="text-xs text-white/30 italic mt-1 ml-4" data-id="element-3451" data-mp="1gay5l2ekr1j3">
                                        📝 {item.notes}
                                      </p>}
                                  </div>)}
                            </div>
                            {order.notes && <div className="mt-3 pt-3 border-t border-white/5" data-id="element-3452" data-mp="xcluvc80ljle">
                                <p className="text-xs text-white/40 italic" data-id="element-3453" data-mp="2npkzjuoi8kqd">
                                  📝 Nota: {order.notes}
                                </p>
                              </div>}
                          </div>

                          {/* Actions */}
                          <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-3454" data-mp="c2hgihs4rny1">
                            {caprichoStatus === 'nuevo' && <button onClick={() => handleOrderAction(order.id, 'capricho_receive')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-[#8B5E3C] hover:bg-[#A0704A] text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3455" data-mp="pv79j662tt4n">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-3456" data-mp="35ltose9kk0wr">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-3457" data-mp="19i5f1gswcu86" />
                                    Procesando...
                                  </span> : '✅ PEDIDO RECIBIDO'}
                              </button>}
                            {caprichoStatus === 'en_preparacion' && <button onClick={() => handleOrderAction(order.id, 'capricho_complete')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3458" data-mp="1xfku0p7m4ecs">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-3459" data-mp="3vrcz2999nt7o">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-3460" data-mp="3rnor3pgq2vlw" />
                                    Procesando...
                                  </span> : '🎉 PEDIDO COMPLETADO'}
                              </button>}
                            {caprichoStatus === 'listo' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3461" data-mp="9gqsw35bzl6b">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-3462" data-mp="5jjtcvzgrdg4">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-3463" data-mp="5hgudmgc29z9" />
                                    Procesando...
                                  </span> : '🚀 ENTREGAR'}
                              </button>}
                            {order.delivery_type === 'dine_in' && <button onClick={() => handleOpenPay(order)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3464" data-mp="34xdjhrww48hk">
                                <Receipt className="w-4 h-4" data-id="element-3465" data-mp="3bcud1uxvx3os" />
                                COBRAR / FACTURAR
                              </button>}
                            {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-3466" data-mp="2sgksqpwe3gwc">
                                Cancelar pedido
                              </button>}
                          </div>
                        </div>;
            }
            // ── Standard card (non-Capricho) ──
            return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', borderColor, waitMinutes >= 15 && order.status !== 'ready' ? 'shadow-[0_0_15px_rgba(239,68,68,0.15)]' : '')} data-id="element-3467" data-mp="fqfksozp26zc">
                        {/* Order Header */}
                        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2 bg-white/[0.02]" data-id="element-3468" data-mp="3j7pb2r8efki8">
                          <div className="flex items-center gap-2 min-w-0" data-id="element-3469" data-mp="1ypdgd7dmr0o5">
                            <span className="text-lg font-black text-white tabular-nums" data-id="element-3470" data-mp="23qknr5bv1xua">
                              #{getShortOrderNumber(order.order_number)}
                            </span>
                            {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-3471" data-mp="2ie5paut37p03">
                                <Bike className="w-3 h-3" data-id="element-3472" data-mp="2mivksiju31kh" /> Delivery
                              </span> : order.delivery_type === 'pickup' ? <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-3473" data-mp="2weggjyyb75ls">
                                <ShoppingBag className="w-3 h-3" data-id="element-3474" data-mp="120pataj3p5vm" /> Llevar
                              </span> : <span className="bg-white/10 text-white/70 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-3475" data-mp="u115zhmtzmn9">
                                <Store className="w-3 h-3" data-id="element-3476" data-mp="q10r5vvtl9fo" />{' '}
                                {resolveTableLabel(tables, order.table_id) || `Mesa ${order.table_id || ''}`}
                              </span>}
                          </div>
                          <span className={cn('text-xl font-black tabular-nums tracking-tighter shrink-0', waitMinutes >= 15 && order.status !== 'ready' ? 'text-red-500 animate-pulse' : waitMinutes >= 10 && order.status !== 'ready' ? 'text-amber-500' : 'text-emerald-500')} data-id="element-3477" data-mp="18xid1wnzmf40">
                            {waitMinutes}m
                          </span>
                        </div>

                        {/* Customer & Total */}
                        <div className="px-4 py-2 border-b border-white/5 flex justify-between items-center" data-id="element-3478" data-mp="21bvyetiaiv4k">
                          <p className="text-sm text-white/70 font-bold truncate pr-2" data-id="element-3479" data-mp="26a77h20rh1lq">
                            {order.customer_name}
                          </p>
                          <p className="text-sm font-black text-[#F4D35E] shrink-0" data-id="element-3480" data-mp="1rjzmofna76hv">
                            ₡{order.total.toLocaleString()}
                          </p>
                        </div>

                        {/* Status Banner — shows current phase. Hidden for Rancho (id=28). */}
                        {!isRanchoSanFrancisco && (order.status === 'preparing' || hasKitchenActivity && order.status !== 'ready') && <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20" data-id="element-3481" data-mp="2gxgtv9y9tqp1">
                              <div className="flex items-center justify-between mb-2" data-id="element-3482" data-mp="2bxcclhpjgrxr">
                                <span className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-3483" data-mp="276vz5xlpx6be">
                                  <ChefHat className="w-3.5 h-3.5" data-id="element-3484" data-mp="2279jlhif3dzf" />
                                  {allItemsReady ? 'Cocina terminó' : 'En cocina'}
                                </span>
                                <span className="text-sm font-black text-amber-400 tabular-nums" data-id="element-3485" data-mp="1o81gwen1pnqh">
                                  {overallPercentage}%
                                </span>
                              </div>
                              <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-3486" data-mp="3il9gejlyunig">
                                <div className={cn('h-full rounded-full transition-all duration-700 ease-out', overallPercentage === 100 ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                    width: `${overallPercentage}%`
                  }} data-id="element-3487" data-mp="3do9d2ggi3yv1" />
                              </div>
                            </div>}

                        {!isRanchoSanFrancisco && (order.status === 'new' || order.status === 'accepted') && !hasKitchenActivity && <div className="px-4 py-3 bg-blue-500/10 border-b border-blue-500/20" data-id="element-3488" data-mp="rgmln82vy8qr">
                              <span className="text-xs font-black text-blue-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-3489" data-mp="we4xp7c7942z">
                                <Clock className="w-3.5 h-3.5" data-id="element-3490" data-mp="2sg80tgta91dc" />
                                Nuevo pedido — Pendiente de enviar a cocina
                              </span>
                            </div>}

                        {!isRanchoSanFrancisco && order.status === 'ready' && <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20" data-id="element-3491" data-mp="2xf1jdnnffikd">
                            <span className="text-xs font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-3492" data-mp="29p72muzzl9uu">
                              <CheckCircle2 className="w-3.5 h-3.5" data-id="element-3493" data-mp="2ik508fe2xasi" />
                              ¡Listo para entregar!
                            </span>
                          </div>}

                        {/* Station Progress — hidden for Rancho (id=28). */}
                        {!isRanchoSanFrancisco && showProgressView && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-3494" data-mp="1m060tqlnswyj">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-3495" data-mp="1bwh61e45hv6r">
                              Progreso por estación
                            </h4>
                            {stationProgress.size === 0 ? <p className="text-sm text-white/40 italic" data-id="element-3496" data-mp="16yfrn6ho6k1m">
                                Sin items
                              </p> : <div className="space-y-3" data-id="element-3497" data-mp="1zncwo7wvekcf">
                                {Array.from(stationProgress.entries()).map(([code, progress]) => {
                    const isComplete = progress.ready === progress.total;
                    const percentage = Math.round(progress.ready / progress.total * 100);
                    return <div key={code} className="space-y-1.5" data-id="element-3498" data-mp="3rqq0rmeggl9a">
                                        <div className="flex justify-between items-center text-sm" data-id="element-3499" data-mp="3mp0rocasmhnu">
                                          <span className={cn('font-bold truncate pr-2', isComplete ? 'text-emerald-400' : 'text-white/80')} data-id="element-3500" data-mp="3hrqqjze1khk6">
                                            {isComplete ? '✅' : '🔥'}{' '}
                                            {progress.name}
                                          </span>
                                          <span className={cn('font-black tabular-nums shrink-0', isComplete ? 'text-emerald-400' : 'text-amber-400')} data-id="element-3501" data-mp="x2kt91z2ti4z">
                                            {progress.ready} / {progress.total}
                                          </span>
                                        </div>
                                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-3502" data-mp="2ytp07yvfk5cr">
                                          <div className={cn('h-full rounded-full transition-all duration-500', isComplete ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                          width: `${percentage}%`
                        }} data-id="element-3503" data-mp="33lyvzvj87zmy" />
                                        </div>
                                      </div>;
                  })}
                              </div>}
                          </div>}

                        {/* Items list. Rancho (id=28) lo muestra siempre
                            sin etiqueta de estación. El resto mantiene
                            el flujo original. */}
                        {(isRanchoSanFrancisco || (order.status === 'new' || order.status === 'accepted') && !hasKitchenActivity) && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-3504" data-mp="21pcq7giu4bqy">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-3505" data-mp="1wooz6uulvuwq">
                              Items del pedido
                            </h4>
                            <div className="space-y-1.5" data-id="element-3506" data-mp="1mrahkrwy502u">
                              {order.items.filter((item: any) => item.status !== 'cancelled').map((item: any) => <div key={item.id} data-id="element-3507" data-mp="2aohja7bmncvq">
                                    <div className="flex justify-between text-sm" data-id="element-3508" data-mp="2amwpwl11tzzg">
                                      <span className="text-white/70 truncate pr-2" data-id="element-3509" data-mp="25pdaviwj1j6g">
                                        {item.quantity}x{' '}
                                        {item.item_name_snapshot}
                                      </span>
                                      {!isRanchoSanFrancisco && <span className="text-white/40 text-xs shrink-0" data-id="element-3510" data-mp="wje52lptqm2u">
                                          {stations.find(s => s.code === item.station_code)?.name || 'General'}
                                        </span>}
                                    </div>
                                    {/* Detalle de opciones por ítem — ej. tipo de
                                     natural (limonada), de qué es el casado
                                     (pollo / costilla), etc. Solo visual. */}
                                    {item.options && item.options.length > 0 && <div className="mt-0.5 ml-4 space-y-0.5" data-id="element-3511" data-mp="1y76qfs1s9cuo">
                                          {item.options.map((opt: any, idx: number) => <div key={idx} className="text-xs text-amber-400/80" data-id="element-3512" data-mp="3nl6i4dh9zu4t" data-mpi={idx}>
                                                · {opt.option_name}
                                              </div>)}
                                        </div>}
                                    {item.notes && <p className="text-xs text-white/40 italic mt-0.5 ml-4" data-id="element-3513" data-mp="k4psqvkumbw5">
                                        📝 {item.notes}
                                      </p>}
                                  </div>)}
                            </div>
                          </div>}

                        {/* Action Footer */}
                        <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-3514" data-mp="1d0pa1rcpeyzv">
                          {isRanchoSanFrancisco && <div className="flex flex-col gap-2" data-id="element-3515" data-mp="1i0thkrvmwjdy">
                              {/* Adicionar productos a la orden ya realizada.
                                Aplica a CUALQUIER canal de Rancho — Mesa,
                                Para llevar y Express — mientras la orden
                                siga activa (no cobrada / cancelada). Reusa
                                el mismo CabanaAddItemsModal que ya maneja
                                Rancho (id 28), incluyendo el print job de
                                adición. No toca pagos ni ítems existentes. */}
                              {!['cancelled', 'delivered', 'out_for_delivery'].includes(order.status) && <button onClick={() => setAddItemsOrderId(order.id)} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all" data-id="element-3516" data-mp="3u9iiwsw6e7x4">
                                  <Plus className="w-4 h-4" data-id="element-3517" data-mp="3gbbu0297fi9l" />
                                  ADICIONAR PRODUCTOS
                                </button>}
                              <button onClick={() => setRanchoModifyOrderId(order.id)} className="w-full h-12 rounded-xl text-sm font-black bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 flex items-center justify-center gap-2 active:scale-95 transition-all" data-id="element-3518" data-mp="36kr0uucav5y2">
                                <Pencil className="w-4 h-4" data-id="element-3519" data-mp="2ivj3kmt4d6ca" />
                                EDITAR / MODIFICAR ORDEN
                              </button>
                              <button onClick={() => handleImprimirComandaRancho(order)} disabled={printingOrderIds.has(String(order.id))} className="w-full h-12 rounded-xl text-sm font-black bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3520" data-mp="28yedwd02esqq">
                                {printingOrderIds.has(String(order.id)) ? <span className="flex items-center gap-2" data-id="element-3521" data-mp="3pccxzkilt2fs">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-3522" data-mp="3pehbmu6b1ft9" />
                                    Imprimiendo...
                                  </span> : <>
                                    <Printer className="w-4 h-4" data-id="element-3523" data-mp="1np9ynuo0gns6" />
                                    IMPRIMIR COMANDA
                                  </>}
                              </button>
                              <button onClick={() => handleOpenPay(order)} className="w-full h-12 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform" data-id="element-3524" data-mp="q089mt8p1ame">
                                <Receipt className="w-4 h-4" data-id="element-3525" data-mp="2fctn1ky5lp3k" />
                                COBRAR Y FACTURAR
                              </button>
                            </div>}
                          {!isRanchoSanFrancisco && (order.status === 'new' || order.status === 'accepted') && <button onClick={() => handleOrderAction(order.id, order.status === 'new' ? 'send_to_kitchen' : 'start_preparation')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3526" data-mp="insbuufufzya">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-3527" data-mp="1pby7d6rakjyt">
                                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-3528" data-mp="1kilvlx7po7nh" />
                                    Enviando a cocina...
                                  </span> : '🔥 ENVIAR A COCINA'}
                              </button>}
                          {!isRanchoSanFrancisco && order.status === 'preparing' && <button onClick={() => handleOrderAction(order.id, 'mark_ready')} disabled={isUpdatingOrder || !allItemsReady} className={cn('w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all', allItemsReady ? 'bg-emerald-500 hover:bg-emerald-400 text-black active:scale-95 shadow-[0_0_15px_rgba(16,185,129,0.2)]' : 'bg-white/5 text-white/30 cursor-not-allowed')} data-id="element-3529" data-mp="1z6q6wg4fpgw8">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-3530" data-mp="3qzz9z6dlvhlt">
                                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-3531" data-mp="8tuor2rrnswt" />
                                    Procesando...
                                  </span> : allItemsReady ? '✅ MARCAR LISTO' : <span className="flex items-center gap-2" data-id="element-3532" data-mp="26lfdou20gsdh">
                                    <span className="w-4 h-4 border-2 border-white/20 border-t-amber-400 rounded-full animate-spin" data-id="element-3533" data-mp="1wgyxu0oj7b9o" />
                                    EN COCINA — {overallPercentage}% LISTO
                                  </span>}
                              </button>}
                          {!isRanchoSanFrancisco && (order.status === 'ready' || order.status === 'searching_driver') && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3534" data-mp="1hooqx5d0s76d">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-3535" data-mp="2wn2qini4c3am">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-3536" data-mp="2rnzkazqejr12" />
                                    Procesando...
                                  </span> : order.delivery_type === 'delivery' ? '🚀 MARCAR COMO ENVIADO' : '🚀 ENTREGAR AL CLIENTE'}
                              </button>}
                          {!isRanchoSanFrancisco && (order.delivery_type === 'dine_in' || isCabanaParrillera && (order.delivery_type === 'delivery' || order.delivery_type === 'pickup')) && <button onClick={() => handleOpenPay(order)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3537" data-mp="ei2z43bbv4em">
                                <Receipt className="w-4 h-4" data-id="element-3538" data-mp="xg5io2l3w951" />
                                COBRAR / FACTURAR
                              </button>}
                          {!isRanchoSanFrancisco && ['new', 'accepted', 'preparing'].includes(order.status) && !isCabanaParrillera && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-3539" data-mp="1g7ydm2jqk4fe">
                                Cancelar pedido
                              </button>}

                          {/* La Cabaña Parrillera — exclusive cashier actions.
                           Add products to the same comanda OR modify/cancel.
                           Available for any active status (incl. ready). */}
                          {isCabanaParrillera && !['cancelled', 'delivered', 'out_for_delivery'].includes(order.status) && <>
                                <button onClick={() => setAddItemsOrderId(order.id)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3540" data-mp="3ixauxh1tpbug">
                                  ➕ ADICIONAR PRODUCTOS
                                </button>
                                <button onClick={() => setModifyOrderId(order.id)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-3541" data-mp="3rweh4twae7d6">
                                  ✏️ MODIFICAR / CANCELAR
                                </button>
                              </>}
                        </div>
                      </div>;
          }) : <div className="col-span-full py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-3542" data-mp="p46cczm3i5ho">
                    <CheckCircle2 className="w-16 h-16 text-white/10 mb-4" data-id="element-3543" data-mp="fbihwbil8dm4" />
                    <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-3544" data-mp="acfnrm45brsx">
                      {activeFilter !== 'all' ? 'Sin pedidos en esta categoría' : 'Todo bajo control'}
                    </h3>
                    <p className="text-sm" data-id="element-3545" data-mp="1elqa7unas46">
                      {activeFilter !== 'all' ? 'No hay pedidos que coincidan con este filtro.' : 'No hay pedidos activos en este momento.'}
                    </p>
                    {activeFilter !== 'all' && <button onClick={() => setActiveFilter('all')} className="mt-4 px-4 py-2 rounded-lg bg-white/10 text-white/60 text-sm font-bold hover:bg-white/15 transition-colors" data-id="element-3546" data-mp="2d1pcftmbg50q">
                        Ver todos los pedidos
                      </button>}
                  </div>;
        })()}
        </div>

        {/* PayTableModal */}
        <PayTableModal isOpen={showPayModal} onClose={() => {
        setShowPayModal(false);
        setPayTable(null);
      }} table={payTable} restaurantId={restaurant?.id} onSuccess={allPaid => {
        refetchTables();
        if (allPaid && payTable?.current_order?.id) {
          // Mark the order as delivered when fully paid
          deliverOrder(payTable.current_order.id).then(() => {
            addToast('success', '✅ Pedido cobrado y cerrado');
          });
        } else {
          addToast('success', '💵 Pago parcial registrado');
        }
      }} createdBy={tenantCtx?.userId || null} data-id="element-3547" data-mp="1y9x4i2oupt91" />

        {/* La Cabaña Parrillera — Modify / Cancel modal */}
        {isCabanaParrillera && <CabanaModifyOrderModal isOpen={!!modifyOrderId} onClose={() => setModifyOrderId(null)} orderId={modifyOrderId} restaurantId={restaurant?.id} onChanged={() => {
        refetchOrders();
        refetchTables();
      }} onCancelOrder={async orderId => {
        const res = await cancelOrder(orderId);
        if (res.success) {
          addToast('success', '❌ Pedido cancelado');
        } else if (res.error) {
          addToast('error', res.error);
        }
        return res;
      }} data-id="element-3548" data-mp="mv33fmhn3a2l" />}

        {/* Rancho San Francisco — Editar / Modificar orden (reusa el
            mismo modal genérico de Cabaña). Cancelar la orden completa
            sigue disponible desde acá. */}
        {isRanchoSanFrancisco && <CabanaModifyOrderModal isOpen={!!ranchoModifyOrderId} onClose={() => setRanchoModifyOrderId(null)} orderId={ranchoModifyOrderId} restaurantId={restaurant?.id} onChanged={() => {
        refetchOrders();
        refetchTables();
      }} onCancelOrder={async orderId => {
        const res = await cancelOrder(orderId);
        if (res.success) {
          addToast('success', '❌ Pedido cancelado');
        } else if (res.error) {
          addToast('error', res.error);
        }
        return res;
      }} data-id="element-3549" data-mp="1f9g97azglhi1" />}

        {/* Adicionar productos a una comanda existente.
            La Cabaña Parrillera y Rancho San Francisco comparten el mismo
            modal (CabanaAddItemsModal ya trae la rama isRancho para el
            print job de adición). Cubre Mesa, Para llevar y Express: el
            modal opera sólo por orderId, así que no depende de que la
            orden tenga mesa. */}
        {(isCabanaParrillera || isRanchoSanFrancisco) && <CabanaAddItemsModal isOpen={!!addItemsOrderId} onClose={() => setAddItemsOrderId(null)} orderId={addItemsOrderId} restaurantId={restaurant?.id} onAdded={() => {
        addToast('success', 'Productos adicionados');
        refetchOrders();
        refetchTables();
      }} data-id="element-3550" data-mp="1rt6dp0kvmvyh" />}

        <VoidConfirmModal isOpen={voidModalState.isOpen} onClose={() => setVoidModalState({
        isOpen: false,
        orderId: null,
        originalAmount: 0
      })} onConfirm={handleVoidConfirm} title="Anular Pedido" description="Por favor, indica quién autoriza y el motivo de la anulación del pedido. Esta acción quedará registrada en el reporte de caja." data-id="element-3551" data-mp="18x6uof9vn2r1" />

        {/* ═══ TOAST NOTIFICATIONS ═══ */}
        {toasts.length > 0 && <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-3552" data-mp="1d828qocxw1py">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-3553" data-mp="188y1w4dhmg2s">
                <span className="flex-1" data-id="element-3554" data-mp="1z0wsgeca5gm1">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-3555" data-mp="1yyk4nkjucc12">
                  <X className="w-4 h-4" data-id="element-3556" data-mp="28xjev7k98fq6" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}